import { createEvent, createStore, createEffect, sample } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { PagePatterns, patternToType } from "@features/shared/page-type/page-types";
import { $activeRoute, $routeParams, routeParamsChanged, routePathNameChanged } from "@features/app/model";

export type PageDescriptor = { type: string; metadata: Record<string, any> };

type RoutePattern<TType extends string> = {
    type: TType;
    pattern: string;
    parse?: (params: Record<string, string>) => PageDescriptor;
};

function normalizePathname(pathname: string): string {
    if (!pathname) return "/";
    if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
    return pathname;
}

function matchPattern(pathname: string, pattern: string): Record<string, string> | null {
    const pathSegments = normalizePathname(pathname).split("/");
    const patternSegments = normalizePathname(pattern).split("/");

    if (pathSegments.length !== patternSegments.length) return null;

    const params: Record<string, string> = {};

    for (let i = 0; i < patternSegments.length; i += 1) {
        const p = patternSegments[i];
        const s = pathSegments[i];

        if (p.startsWith(":")) {
            params[p.slice(1)] = s;
        } else if (p !== s) {
            return null;
        }
    }

    return params;
}

function toInt(value: string | undefined): number | null {
    if (!value) return null;
    const num = Number(value);
    return Number.isInteger(num) ? num : null;
}

const defaultPatterns: RoutePattern<string>[] = [];

export const registerRoutePattern = createEvent<RoutePattern<string>>();
export const setRoutePatterns = createEvent<RoutePattern<string>[]>();
export const registerRoutePatternByKey = createEvent<keyof typeof PagePatterns>();

export const $routePatterns = createStore<RoutePattern<string>[]>(defaultPatterns)
    .on(registerRoutePattern, (s, p) => {
        const next = [...s, p];
        return next.sort((a, b) => b.pattern.split("/").length - a.pattern.split("/").length);
    })
    .on(setRoutePatterns, (_, patterns) => {
        return [...patterns].sort((a, b) => b.pattern.split("/").length - a.pattern.split("/").length);
    })
    .on(registerRoutePatternByKey, (s, key) => {
        const pattern = PagePatterns[key];
        const type = patternToType(pattern);
        const next = [...s, { type, pattern }];
        return next.sort((a, b) => b.pattern.split("/").length - a.pattern.split("/").length);
    });

export const resolvePageFromUrlFx = createEffect(
    ({
        pathname,
        params,
        patterns
    }: {
        pathname: string;
        params: Record<string, string | string[]>;
        patterns: RoutePattern<string>[];
    }): PageDescriptor => {
        const normalized = normalizePathname(pathname);
        for (const p of patterns) {
            const match = matchPattern(normalized, p.pattern);
            if (match) {
                if (p.parse) return p.parse(match);
                return { type: p.type, metadata: match } as PageDescriptor;
            }
        }
        if (normalized === "/") return { type: "unknown", metadata: {} };
        const segments = normalized.split("/").filter(Boolean);
        const paramValues = Object.values(params)
            .flatMap((v) => (Array.isArray(v) ? v : [v]))
            .map(String);
        const staticSegments = segments.filter((seg) => !paramValues.includes(seg) && !/^\d+$/.test(seg));
        const type = staticSegments.length ? staticSegments.join(".") : "unknown";
        const metaEntries = Object.entries(params).map(([k, v]) => {
            const value = Array.isArray(v) ? v[0] : v;
            const maybeNum = toInt(String(value));
            return [k, maybeNum ?? value];
        });
        const metadata = Object.fromEntries(metaEntries) as Record<string, any>;
        return { type, metadata } as PageDescriptor;
    }
);

export const $pageDescriptor = createStore<PageDescriptor>({ type: "unknown", metadata: {} });

const contract = new ModelContract(
    { $pageDescriptor, $routePatterns },
    { registerRoutePattern, setRoutePatterns, registerRoutePatternByKey },
    { resolvePageFromUrlFx },
    {}
);

export type IPageTypeContract = typeof contract;

export class PageTypeModule extends ModuleDescription<IPageTypeContract> {
    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            clock: routePathNameChanged,
            source: { patterns: $routePatterns, params: $routeParams },
            fn: ({ patterns, params }, pathname) => ({ pathname, params, patterns }),
            target: resolvePageFromUrlFx
        });

        sample({
            clock: routeParamsChanged,
            source: { patterns: $routePatterns, pathname: $activeRoute, params: $routeParams },
            fn: ({ patterns, pathname, params }) => ({ pathname, params, patterns }),
            target: resolvePageFromUrlFx
        });

        sample({
            clock: resolvePageFromUrlFx.doneData,
            target: $pageDescriptor
        });
    }
}
